import type { ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';

import { isAdmin } from '../shared/index.js';

import { useSession } from '../auth/session.js';
import { Spinner } from '../components/ui.js';
import { AccountPage } from '../pages/account.js';
import { BoardPage } from '../pages/board-page.js';
import { DashboardPage } from '../pages/dashboard.js';
import { MyRequestsPage } from '../pages/my-requests.js';
import { BoardSetupPage } from '../pages/settings/board-setup.js';
import { DepartmentsPage } from '../pages/settings/departments.js';
import { JobRolesPage } from '../pages/settings/job-roles.js';
import { UsersPage } from '../pages/settings/users.js';
import { ResetPasswordPage } from '../pages/reset-password.js';
import { SignInPage } from '../pages/sign-in.js';
import { TicketLinkPage } from '../pages/ticket-link.js';
import { Shell } from './shell.js';

function FullPageSpinner(): ReactNode {
  return (
    <div className="grid min-h-dvh place-items-center text-muted">
      <Spinner className="size-6" />
    </div>
  );
}

function Protected({ children }: { readonly children: ReactNode }): ReactNode {
  const { user, isLoading } = useSession();
  const location = useLocation();

  if (isLoading) return <FullPageSpinner />;

  if (user === null) {
    return <Navigate to="/sign-in" replace state={{ from: location.pathname }} />;
  }

  if (user.mustChangePassword && location.pathname !== '/account') {
    return <Navigate to="/account" replace />;
  }

  return <Shell>{children}</Shell>;
}

function AdminOnly({ children }: { readonly children: ReactNode }): ReactNode {
  const { principal } = useSession();

  if (principal !== null && !isAdmin(principal)) {
    return <Navigate to="/" replace />;
  }

  return children;
}

export function App(): ReactNode {
  return (
    <Routes>
      <Route path="/sign-in" element={<SignInPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route
        path="/"
        element={
          <Protected>
            <DashboardPage />
          </Protected>
        }
      />
      <Route
        path="/account"
        element={
          <Protected>
            <AccountPage />
          </Protected>
        }
      />
      <Route
        path="/my-requests"
        element={
          <Protected>
            <MyRequestsPage />
          </Protected>
        }
      />
      <Route
        path="/tickets/:id"
        element={
          <Protected>
            <TicketLinkPage />
          </Protected>
        }
      />
      <Route
        path="/boards/:slug"
        element={
          <Protected>
            <BoardPage />
          </Protected>
        }
      />
      <Route
        path="/settings/boards"
        element={
          <Protected>
            <AdminOnly>
              <BoardSetupPage />
            </AdminOnly>
          </Protected>
        }
      />
      <Route
        path="/settings/users"
        element={
          <Protected>
            <AdminOnly>
              <UsersPage />
            </AdminOnly>
          </Protected>
        }
      />
      <Route
        path="/settings/departments"
        element={
          <Protected>
            <AdminOnly>
              <DepartmentsPage />
            </AdminOnly>
          </Protected>
        }
      />
      <Route
        path="/settings/positions"
        element={
          <Protected>
            <AdminOnly>
              <JobRolesPage />
            </AdminOnly>
          </Protected>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
